import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { persistQueryClient } from '@tanstack/react-query-persist-client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { broadcastQueryClient } from '@tanstack/query-broadcast-client-experimental'
import { createSharedWorkerPersister } from '@sjpnz/query-shared-worker-persister'
import { routeTree } from '../routeTree.gen'
import type { QueryClient } from '@tanstack/react-query'
import type { i18n } from 'i18next'
import { createQueryClient } from '@/lib/query-client'
import { DefaultErrorComponent } from '@/components/shared/route-error'
import {
  LoadingLogo,
  LoadingScreen,
  useHasSidebar,
} from '@/components/shared/loading-logo'

export type RouterContext = {
  auth: ReturnType<typeof useAuth0>
  i18n: i18n
  queryClient: QueryClient
}

function DefaultPendingComponent() {
  const hasSidebar = useHasSidebar()

  if (hasSidebar) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingLogo />
      </div>
    )
  }

  return <LoadingScreen />
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultErrorComponent: DefaultErrorComponent,
  defaultPendingComponent: DefaultPendingComponent,
  defaultPendingMinMs: 500,
  defaultPendingMs: 1_000,
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  context: {
    auth: undefined!,
    i18n: undefined!,
    queryClient: undefined!,
  },
})

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.graphqlClientIntegration({ endpoints: [/\/graphql/] }),
  ],
  tracesSampleRate: 1.0,
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: false,
    httpBodies: [],
    graphQL: { document: true, variables: false },
    stackFrameVariables: false,
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const persister = createSharedWorkerPersister({ key: 'VX_HEART' })

export function AppRouter() {
  const auth = useAuth0()
  const { i18n } = useTranslation()
  const queryClient = useMemo(() => {
    const client = createQueryClient()
    const hash = import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || 'dev'
    broadcastQueryClient({
      queryClient: client,
      broadcastChannel: `vx-heart-${hash}`,
    })
    persistQueryClient({ queryClient: client, persister, buster: hash })

    return client
  }, [])

  if (auth.isLoading) {
    return <LoadingScreen />
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} context={{ auth, i18n, queryClient }} />
    </QueryClientProvider>
  )
}
